import { UserProfile } from './auth';

// Evaluator-specific types based on the API endpoints documentation

export interface Evaluator {
  id: string;
  name: string;
  email: string;
  role: 'evaluator';
  isExternal: boolean;
  isConfirmed: boolean;
  lastLoginAt: string | null;
  userProfile: UserProfile | null;
  evaluationStats?: {
    totalAssignments: number;
    completedEvaluations: number;
    pendingEvaluations: number;
  };
}

export interface DeclineAssignmentRequest {
  reason: 'thematic' | 'conflict' | 'other';
  note?: string;
}

export interface EvaluatorAssignment {
  id: string;
  applicationId: string;
  evaluatorId: string;
  assignedBy: string;
  assignedAt: string;
  acceptedAt: string | null;
  status: 'pending' | 'accepted_not_started' | 'in_progress' | 'completed' | 'declined' | 'conflict_declared';
  evaluationSubmittedAt: string | null;
  conflictDeclared: boolean;
  conflictStatus: 'no_conflict' | 'conflict_declared' | null;
  conflictDetails?: string | null;
  conflictDeclaredAt?: string | null;
  declineReason?: 'thematic' | 'conflict' | 'other' | null;
  declineNote?: string | null;
  evaluator: {
    id: string;
    name: string;
    email: string;
    isExternal: boolean;
    isConfirmed: boolean;
  };
  assignedByUser: {
    id: string;
    name: string;
    email: string;
  };
  evaluation?: {
    id: string;
    submittedAt: string | null;
  };
}

export interface EvaluationCriterion {
  score: number;
  comments: string | null;
  maxScore: number | null;
}

export interface Evaluation {
  id: string;
  assignmentId: string;
  criteriaEvaluations: Record<string, EvaluationCriterion>;
  budgetComments: string | null;
  overallComments: string | null;
  conflictsDeclared: boolean;
  conflictDetails: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EvaluatorApplication {
  id: string;
  applicationNumber: string;
  status: string;
  projectTitle: string;
  principalInvestigator: string;
  submittedAt: string | null;
  assignment: {
    id: string;
    assignedAt: string;
    acceptedAt: string | null;
    status: 'pending' | 'accepted_not_started' | 'in_progress' | 'completed' | 'declined' | 'conflict_declared';
    evaluationSubmitted: boolean;
    conflictDeclared: boolean;
    conflictStatus: 'no_conflict' | 'conflict_declared' | null;
    conflictDetails?: string | null;
    conflictDeclaredAt?: string | null;
    declineReason?: 'thematic' | 'conflict' | 'other' | null;
    declineNote?: string | null;
  };
  evaluation?: {
    id: string;
    submittedAt: string | null;
  };
}

export interface EvaluatorApplicationDetails extends EvaluatorApplication {
  answers: any; // Application form data with sensitive fields removed
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  files: Array<{
    id: string;
    filename: string;
    originalName: string;
    mimeType: string;
    size: number;
    sectionType: string;
    uploadedAt: string;
  }>;
}

// Admin evaluator management types
export interface AssignEvaluatorRequest {
  evaluatorId?: string; // For existing evaluator
  evaluatorEmail?: string; // For external evaluator
  evaluatorName?: string; // For external evaluator
}

// Unified invitation system
export interface InviteEvaluatorRequest {
  email: string;
  name: string;
  applicationId?: string;
  invitationMessage?: string;
}

export interface InviteEvaluatorResponse {
  evaluatorId: string;
  email: string;
  name: string;
  isNewUser: boolean;
  isExternal: boolean;
  roleUpdated: boolean;
  assignmentId?: string;
  emailSent: boolean;
  message: string;
}

// Password setup types
export interface SetupPasswordRequest {
  email: string;
  token: string;
  password: string;
}

export interface PasswordSetupResponse {
  message: string;
  accountConfirmed: boolean;
}

export interface RequestPasswordSetupRequest {
  email: string;
}

export interface RequestPasswordSetupResponse {
  message: string;
}

// Evaluation form types
export interface CreateEvaluationRequest {
  criteriaEvaluations: Record<string, EvaluationCriterion>;
  budgetComments: string | null;
  overallComments: string | null;
  conflictsDeclared: boolean;
  conflictDetails: string | null;
}

export interface SubmitEvaluationResponse {
  message: string;
  evaluationId: string;
  submittedAt: string;
}

export interface ConflictDeclarationRequest {
  hasConflict: boolean;
  conflictDetails?: string;
}

export interface ConflictDeclarationResponse {
  assignmentId: string;
  applicationId: string;
  applicationNumber: string;
  conflictDeclared: boolean;
  conflictDetails: string | null;
  conflictDeclaredAt: string;
  status: string;
  message: string;
}

// Invitation landing types
export interface InvitationTeamMember {
  name: string;
  role: string;
  affiliation: string;
  institutionType?: string;
  country?: string;
}

export interface InvitationDetails {
  assignmentId: string;
  applicationId: string;
  applicationNumber: string;
  projectTitle: string;
  executiveSummary: string;
  team: InvitationTeamMember[];
  assignedAt: string;
  status: 'pending' | 'accepted_not_started' | 'in_progress' | 'completed' | 'declined' | 'conflict_declared';
  evaluator: {
    id: string;
    name: string;
    email: string;
    isConfirmed: boolean;
    isExternal: boolean;
  };
  // Nota: la landing/correo del evaluador NO revela si es árbitro (3.º evaluador
  // de desempate). El árbitro recibe la invitación normal. El mecanismo de
  // arbitraje vive solo del lado admin (adminService / types/admin.ts).
}

export interface InvitationResponseRequest {
  action: 'accept' | 'decline';
  reason?: 'thematic' | 'conflict' | 'other';
  note?: string;
}

export interface InvitationResponseResponse {
  assignmentId: string;
  status: string;
  evaluatorConfirmed: boolean;
  message: string;
}

// Error types
export class EvaluatorServiceError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string
  ) {
    super(message);
    this.name = 'EvaluatorServiceError';
  }
}